// Private-use characters that carry tracked changes (removed and inserted
// words, and a numbered marker) through the ordinary Markdown parse, so
// WorkspaceMarkdown can show them where they fall in the rendered text.
// knowledge-pages/marks.ts writes them; markdown.tsx reads them.

const CLOSE = 0xe001;
const DEL = 0xe200;
const INS = 0xe400;
const MARK = 0xe600;
/** Sentinel codes per kind: suggestions beyond this many are not marked inline. */
export const MARK_LIMIT = 0x200;
const FIRST = 0xe000;
const END = MARK + MARK_LIMIT;

export type SentinelToken = { kind: 'close' } | { kind: 'del' | 'ins' | 'mark'; index: number };

function sentinelAt(code: number): SentinelToken | null {
  if (code === CLOSE) return { kind: 'close' };
  if (code >= DEL && code < DEL + MARK_LIMIT) return { kind: 'del', index: code - DEL };
  if (code >= INS && code < INS + MARK_LIMIT) return { kind: 'ins', index: code - INS };
  if (code >= MARK && code < MARK + MARK_LIMIT) return { kind: 'mark', index: code - MARK };
  return null;
}

export const openMark = (kind: 'del' | 'ins', index: number) =>
  String.fromCharCode((kind === 'del' ? DEL : INS) + index);
export const closeMark = () => String.fromCharCode(CLOSE);
export const markerOf = (index: number) => String.fromCharCode(MARK + index);

/** A text value as plain runs and the sentinel tokens between them. */
export function splitMarked(value: string): Array<string | SentinelToken> {
  const pieces: Array<string | SentinelToken> = [];
  let run = '';
  for (let at = 0; at < value.length; at += 1) {
    const token = sentinelAt(value.charCodeAt(at));
    if (!token) {
      run += value[at];
      continue;
    }
    if (run) pieces.push(run);
    run = '';
    pieces.push(token);
  }
  if (run) pieces.push(run);
  return pieces;
}

/** Whether `text` already holds any character of the sentinel range. */
export function hasSentinels(text: string) {
  for (let at = 0; at < text.length; at += 1) {
    const code = text.charCodeAt(at);
    if (code >= FIRST && code < END) return true;
  }
  return false;
}

export function countSentinels(text: string) {
  let found = 0;
  for (let at = 0; at < text.length; at += 1) if (sentinelAt(text.charCodeAt(at))) found += 1;
  return found;
}
